import { useEffect, useMemo } from 'react';
import type { ChangeEvent } from 'react';
import {
  describeSlugError,
  validateTenantSlug,
} from '../../../lib/tenant-slug.js';
import type { IntakeShellRenderProps } from './IntakeShell.js';

/**
 * wd-2.3.7 — Step 6 "Domain preference" for the prospect-facing intake portal.
 *
 * Fields mirror `intakeStep6Schema` in `packages/config/src/intake-steps.ts`:
 *
 *   primarySubdomain:   string (intakeSlugRegex)
 *   confirmedSubdomain: literal(true)
 *   cnameIntent: discriminatedUnion on `kind`
 *     - 'none'  → _ack: 'not_interested' | 'not_sure_yet'
 *     - 'later' → desiredHostnameCustomer?, desiredHostnameAdmin?,
 *                  ownsDomain: boolean, _ack: 'phase_2_understood'
 *
 * The `primarySubdomain` seeds from `intake.payload.step1.slug` on first
 * mount (set during Step 1) so the prospect sees the subdomain they already
 * picked and just needs to confirm it. If step 1 wasn't filled yet, the
 * field starts empty and the prospect types their own.
 *
 * Real subdomain uniqueness is enforced at the D3.3 commit-to-tenant step
 * (mirrors the Step 1 slug pattern) — this component only enforces the
 * format rules client-side via the same `validateTenantSlug` helper.
 *
 * The `cnameIntent` discriminated union follows the Step 4 pattern: picking
 * a kind resets fields from the other branch before serializing so the
 * `z.discriminatedUnion('kind', ...)` parse never trips on stale keys.
 */

const CNAME_HOSTNAME_REGEX = /^[a-z0-9.-]+\.[a-z]{2,}$/;

type CnameKind = 'none' | 'later' | '';

const NONE_ACK_OPTIONS = [
  { value: 'not_interested', label: 'Not interested right now' },
  { value: 'not_sure_yet', label: 'Not sure yet — we can revisit later' },
] as const;
type NoneAck = (typeof NONE_ACK_OPTIONS)[number]['value'];

type Step6Draft = {
  primarySubdomain: string;
  confirmedSubdomain: boolean;
  cnameKind: CnameKind;
  // 'none' branch
  noneAck: NoneAck | '';
  // 'later' branch
  desiredHostnameCustomer: string;
  desiredHostnameAdmin: string;
  ownsDomain: boolean;
  phaseTwoAck: boolean;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringField(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readCnameKind(value: unknown): CnameKind {
  return value === 'none' || value === 'later' ? value : '';
}

function readNoneAck(value: unknown): NoneAck | '' {
  return value === 'not_interested' || value === 'not_sure_yet' ? value : '';
}

function readStep6(draft: Record<string, unknown>): Step6Draft {
  const cname = isRecord(draft.cnameIntent) ? draft.cnameIntent : {};
  const kind = readCnameKind(cname.kind);
  return {
    primarySubdomain: stringField(draft.primarySubdomain),
    confirmedSubdomain: draft.confirmedSubdomain === true,
    cnameKind: kind,
    noneAck: kind === 'none' ? readNoneAck(cname._ack) : '',
    desiredHostnameCustomer:
      kind === 'later' ? stringField(cname.desiredHostnameCustomer) : '',
    desiredHostnameAdmin:
      kind === 'later' ? stringField(cname.desiredHostnameAdmin) : '',
    ownsDomain: kind === 'later' && cname.ownsDomain === true,
    phaseTwoAck: kind === 'later' && cname._ack === 'phase_2_understood',
  };
}

function groupIssuesByPath(
  issues: ReadonlyArray<{ path: string; message: string }>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const issue of issues) {
    const key = issue.path === '' ? '_root' : issue.path;
    const list = out[key] ?? [];
    list.push(issue.message);
    out[key] = list;
  }
  return out;
}

function fieldErrorId(field: string): string {
  return `intake-step6-${field}-error`.replace(/[^a-z0-9-]/gi, '-');
}

function describeHostnameError(value: string): string | null {
  if (value.trim() === '') return null;
  return CNAME_HOSTNAME_REGEX.test(value.trim())
    ? null
    : 'Enter a hostname like app.yourbrand.com.';
}

/**
 * Build the cnameIntent sub-payload. Only the selected variant's keys are
 * emitted so a prior `later`→`none` switch doesn't leak stale hostnames
 * into the draft (the server's `z.discriminatedUnion('kind', ...)` would
 * reject them with confusing error paths).
 */
function serializeCnameIntent(
  draft: Step6Draft,
): Record<string, unknown> | undefined {
  if (draft.cnameKind === '') return undefined;
  if (draft.cnameKind === 'none') {
    const out: Record<string, unknown> = { kind: 'none' };
    if (draft.noneAck !== '') out._ack = draft.noneAck;
    return out;
  }
  // 'later'
  const out: Record<string, unknown> = {
    kind: 'later',
    ownsDomain: draft.ownsDomain,
  };
  if (draft.phaseTwoAck) out._ack = 'phase_2_understood';
  if (draft.desiredHostnameCustomer.trim() !== '') {
    out.desiredHostnameCustomer = draft.desiredHostnameCustomer.trim();
  }
  if (draft.desiredHostnameAdmin.trim() !== '') {
    out.desiredHostnameAdmin = draft.desiredHostnameAdmin.trim();
  }
  return out;
}

function serializeStep6(next: Step6Draft): Record<string, unknown> {
  const out: Record<string, unknown> = {
    primarySubdomain: next.primarySubdomain,
  };
  if (next.confirmedSubdomain) out.confirmedSubdomain = true;
  const intent = serializeCnameIntent(next);
  if (intent) out.cnameIntent = intent;
  return out;
}

function readStep1Slug(intakePayload: Record<string, unknown>): string {
  const step1 = isRecord(intakePayload.step1) ? intakePayload.step1 : null;
  if (!step1) return '';
  return stringField(step1.slug);
}

export type Step6DomainProps = IntakeShellRenderProps;

export function Step6Domain({
  stepDraft,
  onChange,
  intake,
  issues,
}: Step6DomainProps): JSX.Element {
  const errorsByPath = useMemo(() => groupIssuesByPath(issues), [issues]);
  const value = useMemo(() => readStep6(stepDraft), [stepDraft]);

  const serverErrorFor = (path: string): string | null => {
    const list = errorsByPath[path];
    return list && list.length > 0 ? list.join(' · ') : null;
  };

  // Seed primarySubdomain from Step 1's slug on first mount when absent.
  // Matches the Step 5 starter-owner-row pattern — the prospect sees the
  // subdomain they picked earlier and only needs to confirm.
  useEffect(() => {
    if (stringField(stepDraft.primarySubdomain) !== '') return;
    const seed = readStep1Slug(intake.payload);
    if (seed === '') return;
    const next: Step6Draft = { ...value, primarySubdomain: seed };
    onChange(serializeStep6(next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slugValidation = validateTenantSlug(value.primarySubdomain);
  const slugClientError =
    value.primarySubdomain.length === 0 || slugValidation.ok
      ? null
      : describeSlugError(slugValidation.error);
  const slugServerError = serverErrorFor('primarySubdomain');
  const confirmedServerError = serverErrorFor('confirmedSubdomain');
  const cnameRootServerError = serverErrorFor('cnameIntent');
  const cnameKindServerError = serverErrorFor('cnameIntent.kind');
  const cnameAckServerError = serverErrorFor('cnameIntent._ack');
  const cnameOwnsServerError = serverErrorFor('cnameIntent.ownsDomain');
  const customerHostnameServerError = serverErrorFor(
    'cnameIntent.desiredHostnameCustomer',
  );
  const adminHostnameServerError = serverErrorFor(
    'cnameIntent.desiredHostnameAdmin',
  );

  const onSubdomainChange = (e: ChangeEvent<HTMLInputElement>): void => {
    // Any edit clears a prior confirmation — the prospect must re-confirm
    // the new value. This mirrors the Step 1 slug confirmation pattern.
    const next: Step6Draft = {
      ...value,
      primarySubdomain: e.target.value,
      confirmedSubdomain: false,
    };
    onChange(serializeStep6(next));
  };

  const canConfirm =
    value.primarySubdomain.length > 0
    && slugValidation.ok
    && !value.confirmedSubdomain;

  const onConfirmSubdomain = (): void => {
    if (!canConfirm) return;
    const next: Step6Draft = { ...value, confirmedSubdomain: true };
    onChange(serializeStep6(next));
  };

  const onKindChange = (kind: CnameKind): void => {
    if (kind === value.cnameKind) return;
    // Switching kinds resets the other branch's fields so the serialized
    // payload never carries stale keys into the discriminated union.
    const next: Step6Draft = {
      ...value,
      cnameKind: kind,
      noneAck: '',
      desiredHostnameCustomer: '',
      desiredHostnameAdmin: '',
      ownsDomain: false,
      phaseTwoAck: false,
    };
    onChange(serializeStep6(next));
  };

  const patchDraft = (patch: Partial<Step6Draft>): void => {
    const next: Step6Draft = { ...value, ...patch };
    onChange(serializeStep6(next));
  };

  const customerHostnameClientError = describeHostnameError(
    value.desiredHostnameCustomer,
  );
  const adminHostnameClientError = describeHostnameError(
    value.desiredHostnameAdmin,
  );

  const primaryDomain = 'animalloversapp.com';
  const customerPreview =
    value.primarySubdomain !== '' && slugValidation.ok
      ? `${value.primarySubdomain}.${primaryDomain}`
      : null;
  const adminPreview =
    value.primarySubdomain !== '' && slugValidation.ok
      ? `${value.primarySubdomain}-admin.${primaryDomain}`
      : null;

  return (
    <form
      className="ferro-form ferro-intake__form"
      aria-label="Step 6 — Domain preference"
      noValidate
      onSubmit={(e) => e.preventDefault()}
    >
      <p className="ferro-muted">
        Pick the subdomain your customers will use on day one. A custom
        domain (e.g. <code>book.yourbrand.com</code>) is a Phase&nbsp;2
        option — you can switch once DNS is ready.
      </p>

      {/* ── Primary subdomain ─────────────────────────────── */}
      <section
        className="ferro-field"
        aria-labelledby="intake-step6-subdomain-heading"
      >
        <h2
          id="intake-step6-subdomain-heading"
          className="ferro-field__label"
        >
          Primary subdomain
        </h2>
        <label className="ferro-field">
          <span className="ferro-field__label">Subdomain</span>
          <input
            type="text"
            className="ferro-field__input"
            value={value.primarySubdomain}
            onChange={onSubdomainChange}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={
              slugClientError || slugServerError ? 'true' : undefined
            }
            aria-describedby={
              slugClientError || slugServerError
                ? fieldErrorId('primary-subdomain')
                : undefined
            }
            data-testid="intake-step6-primary-subdomain"
          />
          {slugServerError ? (
            <span
              id={fieldErrorId('primary-subdomain')}
              role="alert"
              className="ferro-form__error"
            >
              {slugServerError}
            </span>
          ) : slugClientError ? (
            <span
              id={fieldErrorId('primary-subdomain')}
              role="alert"
              className="ferro-form__error"
            >
              {slugClientError}
            </span>
          ) : null}
        </label>

        {customerPreview && adminPreview ? (
          <p
            className="ferro-muted"
            data-testid="intake-step6-subdomain-preview"
          >
            Customer surface: <strong>{customerPreview}</strong>
            <br />
            Admin surface: <strong>{adminPreview}</strong>
          </p>
        ) : null}

        <button
          type="button"
          className="ferro-btn ferro-btn--secondary"
          disabled={!canConfirm}
          onClick={onConfirmSubdomain}
          data-testid="intake-step6-confirm-subdomain"
        >
          {value.confirmedSubdomain
            ? 'Subdomain confirmed'
            : 'Confirm subdomain'}
        </button>

        {confirmedServerError ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step6-confirmed-servererror"
          >
            {confirmedServerError}
          </span>
        ) : null}
      </section>

      {/* ── CNAME intent ──────────────────────────────────── */}
      <section
        className="ferro-field"
        aria-labelledby="intake-step6-cname-heading"
      >
        <h2
          id="intake-step6-cname-heading"
          className="ferro-field__label"
        >
          Custom domain (optional)
        </h2>
        <p className="ferro-muted">
          Want to use your own domain down the road? Tell us your intent
          now and we'll guide you through DNS setup after launch.
        </p>
        <div
          role="radiogroup"
          aria-labelledby="intake-step6-cname-heading"
          className="ferro-field ferro-intake__radioGroup"
          data-testid="intake-step6-cname-kind-group"
        >
          <label className="ferro-field ferro-intake__radio">
            <input
              type="radio"
              name="cname-kind"
              value="none"
              checked={value.cnameKind === 'none'}
              onChange={() => onKindChange('none')}
              data-testid="intake-step6-cname-kind-none"
            />
            <span>No custom domain — stick with the subdomain</span>
          </label>
          <label className="ferro-field ferro-intake__radio">
            <input
              type="radio"
              name="cname-kind"
              value="later"
              checked={value.cnameKind === 'later'}
              onChange={() => onKindChange('later')}
              data-testid="intake-step6-cname-kind-later"
            />
            <span>I want a custom domain (we'll set it up later)</span>
          </label>
        </div>
        {cnameKindServerError ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step6-cname-kind-servererror"
          >
            {cnameKindServerError}
          </span>
        ) : cnameRootServerError ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step6-cname-root-servererror"
          >
            {cnameRootServerError}
          </span>
        ) : null}

        {value.cnameKind === 'none' ? (
          <div
            className="ferro-intake__conditional"
            data-testid="intake-step6-none-block"
          >
            <label className="ferro-field">
              <span className="ferro-field__label">
                Which best describes your situation?
              </span>
              <select
                className="ferro-field__input"
                value={value.noneAck}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  patchDraft({ noneAck: readNoneAck(e.target.value) })
                }
                aria-invalid={cnameAckServerError ? 'true' : undefined}
                data-testid="intake-step6-none-ack"
              >
                <option value="">Select…</option>
                {NONE_ACK_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {cnameAckServerError ? (
                <span
                  role="alert"
                  className="ferro-form__error"
                >
                  {cnameAckServerError}
                </span>
              ) : null}
            </label>
          </div>
        ) : null}

        {value.cnameKind === 'later' ? (
          <div
            className="ferro-intake__conditional"
            data-testid="intake-step6-later-block"
          >
            <label className="ferro-field">
              <span className="ferro-field__label">
                Desired customer hostname (optional)
              </span>
              <input
                type="text"
                className="ferro-field__input"
                value={value.desiredHostnameCustomer}
                placeholder="e.g. book.yourbrand.com"
                autoComplete="off"
                spellCheck={false}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  patchDraft({ desiredHostnameCustomer: e.target.value })
                }
                aria-invalid={
                  customerHostnameClientError || customerHostnameServerError
                    ? 'true'
                    : undefined
                }
                aria-describedby={
                  customerHostnameClientError || customerHostnameServerError
                    ? fieldErrorId('customer-hostname')
                    : undefined
                }
                data-testid="intake-step6-customer-hostname"
              />
              {customerHostnameServerError ? (
                <span
                  id={fieldErrorId('customer-hostname')}
                  role="alert"
                  className="ferro-form__error"
                >
                  {customerHostnameServerError}
                </span>
              ) : customerHostnameClientError ? (
                <span
                  id={fieldErrorId('customer-hostname')}
                  role="alert"
                  className="ferro-form__error"
                >
                  {customerHostnameClientError}
                </span>
              ) : null}
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">
                Desired admin hostname (optional)
              </span>
              <input
                type="text"
                className="ferro-field__input"
                value={value.desiredHostnameAdmin}
                placeholder="e.g. admin.yourbrand.com"
                autoComplete="off"
                spellCheck={false}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  patchDraft({ desiredHostnameAdmin: e.target.value })
                }
                aria-invalid={
                  adminHostnameClientError || adminHostnameServerError
                    ? 'true'
                    : undefined
                }
                aria-describedby={
                  adminHostnameClientError || adminHostnameServerError
                    ? fieldErrorId('admin-hostname')
                    : undefined
                }
                data-testid="intake-step6-admin-hostname"
              />
              {adminHostnameServerError ? (
                <span
                  id={fieldErrorId('admin-hostname')}
                  role="alert"
                  className="ferro-form__error"
                >
                  {adminHostnameServerError}
                </span>
              ) : adminHostnameClientError ? (
                <span
                  id={fieldErrorId('admin-hostname')}
                  role="alert"
                  className="ferro-form__error"
                >
                  {adminHostnameClientError}
                </span>
              ) : null}
            </label>
            <label className="ferro-field ferro-intake__checkbox">
              <input
                type="checkbox"
                checked={value.ownsDomain}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  patchDraft({ ownsDomain: e.target.checked })
                }
                aria-invalid={cnameOwnsServerError ? 'true' : undefined}
                data-testid="intake-step6-owns-domain"
              />
              <span className="ferro-field__label">
                We already own this domain (registered with a registrar).
              </span>
            </label>
            {cnameOwnsServerError ? (
              <span role="alert" className="ferro-form__error">
                {cnameOwnsServerError}
              </span>
            ) : null}
            <label className="ferro-field ferro-intake__checkbox">
              <input
                type="checkbox"
                checked={value.phaseTwoAck}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  patchDraft({ phaseTwoAck: e.target.checked })
                }
                aria-invalid={cnameAckServerError ? 'true' : undefined}
                data-testid="intake-step6-phase-two-ack"
              />
              <span className="ferro-field__label">
                I understand custom-domain setup happens after launch
                (Phase&nbsp;2), not on day one.
              </span>
            </label>
            {cnameAckServerError ? (
              <span
                role="alert"
                className="ferro-form__error"
                data-testid="intake-step6-later-ack-servererror"
              >
                {cnameAckServerError}
              </span>
            ) : null}
          </div>
        ) : null}
      </section>
    </form>
  );
}
